import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowLeft, Star } from "lucide-react";
import user from "../assets/images/Frame 440.png";
import toJalali from "../utils/Time";
import useSwipeToDismiss from "../hooks/useSwipeToDismiss";
import { trackEvent } from "../utils/analytics";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const reviewText = (value: unknown, fallback: string) =>
  typeof value === "string" || typeof value === "number" ? String(value) : fallback;

interface ReviewImage {
  image_id?: string;
  file_url?: string;
  url?: string;
  description?: string | null;
}

type ReviewImageReference = ReviewImage | string;

export interface Review {
  review_id: string;
  user_name: string;
  rate: number;
  comment: string;
  date: string;
  images?: ReviewImageReference[];
  review_images?: ReviewImageReference[];
}

interface ReviewsSectionProps {
  placeId?: string;
  placeName?: string;
  reviewSummary?: string;
  reviews: Review[];
  currentPage: number;
  totalPages: number;
  totalReviews?: number;
  onPageChange: (page: number) => void;
  onLoadAllReviews?: () => Promise<void>;
  isLoading?: boolean;
}

interface StarRatingProps {
  rating: number;
  userName: string;
  date: string;
}

const StarRating: React.FC<StarRatingProps> = ({ rating, userName, date }) => (
  <div className="flex shrink-0 items-center justify-between gap-2 font-myYekanFaNumRegular">
    <div className="flex min-w-0 items-center gap-2">
      <img src={user} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
      <div className="flex min-w-0 items-center gap-2">
        <span className="max-w-[90px] truncate text-xs font-myYekanFaNumDemiBold text-[#222222]">
          {typeof userName === "string" && userName !== "comment" && userName !== "Unknown" ? userName : "کاربر سایت"}
        </span>
        <time className="shrink-0 text-[10px] text-gray-600">{date}</time>
      </div>
    </div>

    <div className="flex shrink-0 items-center gap-0.5" dir="ltr" aria-label={`امتیاز ${rating} از ۵`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3.5 w-3.5 ${star <= Math.round(rating) ? "fill-[#038545] text-[#038545]" : "text-gray-300"}`}
          aria-hidden="true"
        />
      ))}
    </div>
  </div>
);

const reviewImageRequests = new Map<string, Promise<ReviewImage | null>>();

const resolveReviewImage = (image: ReviewImageReference): Promise<ReviewImage | null> => {
  if (typeof image !== "string") return Promise.resolve(image);

  const cachedRequest = reviewImageRequests.get(image);
  if (cachedRequest) return cachedRequest;

  const request = fetch(`${BASE_URL}/api/images/${image}/`)
    .then(async (response) => {
      if (!response.ok) throw new Error("Failed to fetch review image");
      const body = await response.json();
      return body.data ?? body;
    })
    .catch(() => null);

  reviewImageRequests.set(image, request);
  return request;
};

const getReviewImageUrl = (image: ReviewImage) => {
  const source = image.file_url || image.url;
  if (!source) return "";
  return /^(https?:|data:|blob:)/i.test(source) ? source : `${BASE_URL ?? ""}${source}`;
};

const ReviewImages: React.FC<{ review: Review; expanded?: boolean }> = ({ review, expanded = false }) => {
  const imageReferences = (review.images || review.review_images || []).slice(0, expanded ? 6 : 3);
  const [images, setImages] = useState<ReviewImage[]>([]);

  useEffect(() => {
    let isActive = true;

    void Promise.all(imageReferences.map(resolveReviewImage)).then((resolvedImages) => {
      if (isActive) setImages(resolvedImages.filter((image): image is ReviewImage => Boolean(image?.file_url || image?.url)));
    });

    return () => {
      isActive = false;
    };
  }, [review.review_id, expanded, JSON.stringify(imageReferences)]);

  if (!images.length) return null;

return (
  <div className="mt-3 flex flex-wrap gap-2">
    {images.map((image, index) => (
      <div
        key={image.image_id || `${image.file_url || image.url}-${index}`}
        className="h-16 w-16 overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
      >
        <img
          src={getReviewImageUrl(image)}
          alt={image.description || `تصویر دیدگاه ${index + 1}`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
    ))}
  </div>
);
};

const ReviewCardSkeleton: React.FC = () => (
  <div className="h-[200px] w-[86%] min-w-[280px] max-w-[340px] shrink-0 snap-start rounded-xl border border-gray-200 bg-white p-4 shadow-sm tablet:w-auto tablet:min-w-0 tablet:max-w-none">
    <div className="flex animate-pulse flex-col gap-5">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-gray-200" />
        <div className="space-y-2">
          <div className="h-3 w-24 rounded bg-gray-200" />
          <div className="h-2 w-16 rounded bg-gray-200" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 w-full rounded bg-gray-200" />
        <div className="h-3 w-full rounded bg-gray-200" />
        <div className="h-3 w-2/3 rounded bg-gray-200" />
      </div>
    </div>
  </div>
);

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  placeId,
  placeName,
  reviews,
  currentPage,
  totalPages,
  totalReviews,
  onPageChange,
  onLoadAllReviews,
  isLoading = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const previewReviews = reviews;
  const reviewCount = totalReviews ?? reviews.length;
  const { sheetRef, swipeHandlers } = useSwipeToDismiss(
    () => setIsModalOpen(false),
    isModalOpen
  );

  const openAllReviews = () => {
    if (!window.matchMedia("(max-width: 743px)").matches) return;

    trackEvent("click_reviews_view_all", {
      place_id: placeId || "unknown",
      place_name: placeName || "unknown",
      review_count: reviewCount,
      source: "place_detail",
    });
    setSelectedReview(null);
    setIsModalOpen(true);
    void onLoadAllReviews?.();
  };

  const openReview = (review: Review) => {
    if (!window.matchMedia("(max-width: 743px)").matches) return;

    setSelectedReview(review);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen) return;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsModalOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isModalOpen]);

  return (
    <section className="mt-6 flex w-full flex-col bg-white py-4 tablet:py-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <h2 className="text-[1.09rem] font-myYekanFaNumExtraBold leading-[1.5] text-[#222222]">
            نظرات کاربران
          </h2>
          {reviewCount > 0 && (
            <span className="shrink-0 text-[0.66rem] font-myYekanFaNumRegular text-gray-500">
              ({reviewCount})
            </span>
          )}
        </div>
      </div>

      <div className="hide-scrollbar flex items-start snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain pb-2 tablet:grid tablet:grid-cols-1 tablet:overflow-visible tablet:pb-2">
          {isLoading && reviews.length === 0 ? (
            <>
              <ReviewCardSkeleton />
              <ReviewCardSkeleton />
              <ReviewCardSkeleton />
              <ReviewCardSkeleton />
            </>
          ) : reviews.length === 0 ? (
            <div className="w-full shrink-0 py-8 text-center text-[0.77rem] font-myYekanRegular text-gray-600 tablet:col-span-2">
              نظری ثبت نشده است
            </div>
          ) : (
            <>
              {previewReviews.map((review, index) => (
                <button
                  type="button"
                  key={review.review_id}
                  onClick={() => openReview(review)}
                  className={`${index >= 4 ? "hidden tablet:flex" : "flex"} h-[200px] w-[86%] min-w-[280px] max-w-[340px] shrink-0 snap-start flex-col items-stretch justify-start overflow-hidden rounded-xl border border-gray-200 bg-white p-2.5 text-right shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] focus-visible:ring-offset-2 tablet:h-auto tablet:w-full tablet:min-w-0 tablet:max-w-none tablet:rounded-none tablet:border-0 tablet:border-b tablet:border-gray-200 tablet:bg-transparent tablet:p-3 tablet:shadow-none tablet:last:border-b-0`}
                  aria-label={`مشاهده کامل نظر ${reviewText(review.user_name, "کاربر سایت")}`}
                >
                  <StarRating
                    rating={review.rate}
                    userName={reviewText(review.user_name, "کاربر سایت")}
                    date={toJalali(reviewText(review.date, ""))}
                  />
                  <p className="mt-3 line-clamp-3 whitespace-pre-line break-words text-justify text-[0.77rem] font-myYekanRegular leading-[2] text-gray-600 [overflow-wrap:anywhere] tablet:line-clamp-none">
                    {typeof review.comment === "string" ? review.comment : "کاربر تجربه خود را ثبت کرده است."}
                  </p>
                  <ReviewImages review={review} />
                </button>
              ))}

              <button
                type="button"
                onClick={openAllReviews}
                className="flex h-[200px] w-24 shrink-0 snap-start flex-col items-center justify-center gap-2 rounded-xl bg-transparent text-[#222222] transition-colors hover:bg-[#f3fefa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] focus-visible:ring-offset-2 tablet:hidden"
                aria-label="مشاهده همه نظرات"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#038545] text-[#038545]">
                  <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="text-[0.72rem] font-myYekanFaNumDemiBold">مشاهده همه</span>
              </button>
            </>
          )}
      </div>

      {currentPage < totalPages && (
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isLoading}
          className="mx-auto mt-4 hidden min-h-10 items-center justify-center rounded-xl border border-[#038545] px-5 py-2 text-[0.77rem] font-myYekanFaNumDemiBold text-[#068646] transition-colors hover:bg-[#f3fefa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 tablet:flex"
        >
          {isLoading ? "در حال بارگذاری..." : "مشاهده بیشتر"}
        </button>
      )}

      {isModalOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/50 tablet:hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="all-reviews-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div
            ref={sheetRef}
            {...swipeHandlers}
            className="flex max-h-[85dvh] w-full flex-col overflow-hidden rounded-t-[28px] bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl tablet:max-h-[90vh] tablet:max-w-2xl tablet:rounded-2xl tablet:pb-0"
          >
            <span
              className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-300 tablet:hidden"
              aria-hidden="true"
            />
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-4 tablet:px-6">
              <div>
                <h2 id="all-reviews-title" className="text-[1.09rem] font-myYekanFaNumExtraBold text-[#222222]">
                  {selectedReview ? "نظر کاربر" : "همه نظرات کاربران"}
                </h2>
                {!selectedReview && (
                  <p className="mt-1 text-[0.66rem] font-myYekanFaNumRegular text-gray-500">
                    {totalReviews ?? reviews.length} نظر
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
                aria-label="بستن نظرات"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="hide-scrollbar flex-1 space-y-3 overflow-y-auto p-4 tablet:p-6">
              {(selectedReview ? [selectedReview] : reviews).map((review) => (
                <article key={review.review_id} className="rounded-xl p-4">
                  <StarRating
                    rating={review.rate}
                    userName={reviewText(review.user_name, "کاربر سایت")}
                    date={toJalali(reviewText(review.date, ""))}
                  />
                  <p className="mt-3 whitespace-pre-line break-words text-justify text-sm font-myYekanRegular leading-7 text-gray-600 [overflow-wrap:anywhere]">
                    {typeof review.comment === "string" ? review.comment : "کاربر تجربه خود را ثبت کرده است."}
                  </p>
                  <ReviewImages review={review} expanded />
                </article>
              ))}

              {!selectedReview && currentPage < totalPages && (
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={isLoading}
                  className="mx-auto block rounded-full border border-[#038545] px-4 py-2 text-[0.77rem] font-myYekanMedium text-[#068646] hover:bg-[#f3fefa] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? "در حال بارگذاری..." : "بارگذاری نظرات بیشتر"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ReviewsSection;
